import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COMPARE } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Copy,
  Mail,
  Send,
  MessageCircle,
  Linkedin,
  ExternalLink,
} from "lucide-react";

/**
 * Convert-to-lead outreach modal.
 * Given a saved comparison result and one recent hit, prefills a channel-aware
 * follow-up message and offers Email / WhatsApp / LinkedIn / Copy actions.
 */
export default function OutreachModal({ open, onOpenChange, result, hit, shareId, ownerToken }) {
  const [channel, setChannel] = useState("email");
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [userEdited, setUserEdited] = useState(false);
  const [snoozing, setSnoozing] = useState(false);

  // Rebuild the default template lazily as a rendering fallback.
  const defaultMsg = useMemo(
    () => buildTemplate({ result, hit, channel }),
    [result, hit, channel]
  );

  // Re-initialise state whenever a fresh hit is opened. Radix Dialog does NOT
  // fire onOpenChange when the parent flips `open` via prop, so we key off
  // the (open, hit) pair.
  useEffect(() => {
    if (open && hit) {
      const detected = detectChannel(hit.ref_host);
      setChannel(detected);
      setMessage(buildTemplate({ result, hit, channel: detected }));
      setRecipient("");
      setUserEdited(false);
    }
  }, [open, hit, result]);

  // If user switches channels manually, refresh template unless they've edited
  const onChannelChange = (c) => {
    setChannel(c);
    if (!userEdited) setMessage(buildTemplate({ result, hit, channel: c }));
  };

  const activeMessage = message || defaultMsg;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(activeMessage);
      toast.success("Message copied");
    } catch {
      toast.error("Unable to copy — select text and copy manually");
    }
  };

  const send = () => {
    const url = buildDeepLink(channel, recipient, activeMessage);
    if (!url) {
      toast.error("Pick a channel first");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const snooze = async (days) => {
    if (!shareId || !ownerToken) {
      toast.error("Sign in / share must be created before snoozing");
      return;
    }
    setSnoozing(true);
    try {
      const remindAt = new Date(Date.now() + days * 86400 * 1000).toISOString();
      await api.post(
        "/reminders",
        {
          share_id: shareId,
          remind_at: remindAt,
          note: activeMessage.slice(0, 400),
          channel,
          target_ref_host: hit?.ref_host || null,
          target_recipient: recipient || null,
        },
        { headers: { "X-Owner-Token": ownerToken } }
      );
      toast.success(
        `Snoozed — you'll see this on /followups in ${
          days === 1 ? "1 day" : days + " days"
        }`
      );
      window.dispatchEvent(new CustomEvent("imali:reminders-changed"));
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to snooze");
    } finally {
      setSnoozing(false);
    }
  };

  const channels = [
    { id: "email", label: "Email", icon: Mail },
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
    { id: "linkedin", label: "LinkedIn", icon: Linkedin },
    { id: "copy", label: "Copy only", icon: Copy },
  ];

  const recipientPlaceholder = {
    email: "recipient@company.com",
    whatsapp: "+27821234567 (with country code)",
    linkedin: "LinkedIn profile URL (optional — opens search)",
    copy: "Not required",
  }[channel];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={COMPARE.outreachDialog}
        className="max-w-xl bg-background"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Reach out
          </DialogTitle>
          <DialogDescription>
            Follow up with a channel-aware template. Detected source:{" "}
            <span className="font-mono text-foreground">
              {hit?.ref_host || "direct"}
            </span>
            .
          </DialogDescription>
        </DialogHeader>

        {/* Channel picker */}
        <div className="flex flex-wrap gap-2 border-b border-border pb-4">
          {channels.map((c) => {
            const Icon = c.icon;
            const active = channel === c.id;
            return (
              <button
                key={c.id}
                type="button"
                data-testid={COMPARE.outreachChannel(c.id)}
                onClick={() => onChannelChange(c.id)}
                className={`inline-flex items-center gap-2 rounded-full px-3.5 h-9 text-sm border transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground/80 border-border hover:border-foreground/40"
                }`}
              >
                <Icon aria-hidden="true" className="w-3.5 h-3.5" />
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Recipient */}
        {channel !== "copy" && (
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
              Recipient
            </Label>
            <Input
              data-testid={COMPARE.outreachRecipient}
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder={recipientPlaceholder}
              className="mt-1.5"
            />
          </div>
        )}

        {/* Message */}
        <div>
          <Label className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Message
          </Label>
          <Textarea
            data-testid={COMPARE.outreachTextarea}
            value={activeMessage}
            rows={9}
            onChange={(e) => {
              setUserEdited(true);
              setMessage(e.target.value);
            }}
            className="mt-1.5 font-mono text-xs leading-relaxed"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-border">
          {shareId && ownerToken ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
                Snooze
              </span>
              {[
                { days: 1, label: "1d" },
                { days: 3, label: "3d" },
                { days: 7, label: "1w" },
              ].map((s) => (
                <button
                  key={s.days}
                  type="button"
                  data-testid={COMPARE.outreachSnooze(s.days)}
                  onClick={() => snooze(s.days)}
                  disabled={snoozing}
                  className="rounded-full border border-secondary/40 px-3 h-7 text-xs text-secondary hover:bg-secondary/10 disabled:opacity-50 transition-colors"
                >
                  Remind in {s.label}
                </button>
              ))}
            </div>
          ) : (
            <span />
          )}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              data-testid={COMPARE.outreachCopy}
              variant="outline"
              onClick={copy}
              className="rounded-full"
            >
              <Copy aria-hidden="true" className="w-4 h-4 mr-2" />
              Copy message
            </Button>
            {channel !== "copy" && (
              <Button
                data-testid={COMPARE.outreachSend}
                onClick={send}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {channel === "email" ? (
                  <Send aria-hidden="true" className="w-4 h-4 mr-2" />
                ) : (
                  <ExternalLink aria-hidden="true" className="w-4 h-4 mr-2" />
                )}
                {channel === "email" ? "Open email draft" : "Open in " + channelLabel(channel)}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- helpers --------------------------------------------------------------

function channelLabel(c) {
  return { whatsapp: "WhatsApp", linkedin: "LinkedIn", email: "Email", copy: "clipboard" }[c] || c;
}

function detectChannel(refHost) {
  if (!refHost) return "email";
  const h = refHost.toLowerCase();
  if (h.includes("linkedin")) return "linkedin";
  if (h.includes("whatsapp") || h.includes("wa.me")) return "whatsapp";
  if (h.includes("mail") || h.includes("outlook") || h.includes("gmail") || h.includes("yahoo"))
    return "email";
  if (h.includes("slack")) return "copy";
  return "email";
}

function fmtCurrency(amount, code) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  } catch {
    return `${code} ${Math.round(Number(amount) || 0).toLocaleString()}`;
  }
}

function buildTemplate({ result, hit, channel }) {
  if (!result) return "";
  const regions = result.columns
    .map((c) => c.region_short || c.region_name)
    .join(", ");
  const amount = fmtCurrency(result.loan_amount, result.currency_code);
  const source = hit?.ref_host || "the link";

  const brief = `${result.business_name} · ${result.industry_name} · ${amount} · ${result.tenure_months} months`;

  if (channel === "linkedin") {
    return `Hi 👋

I noticed you opened the Imali banking-systems comparison I shared (via ${source}).

Quick recap of the shortlist:
• ${brief}
• Regions compared: ${regions}

Happy to walk through the trade-offs on any of the ${result.columns.length} regions — rates, guarantee schemes and required docs vary quite a bit. Would 15 minutes this week work?

— Sent via Imali`;
  }

  if (channel === "whatsapp") {
    return `Hey! Saw you opened the Imali comparison for ${result.business_name} (${amount}, ${result.tenure_months} mo) — regions: ${regions}.

Want me to line up a call to walk through the fastest / cheapest path? Any of the ${result.columns.length} options can be turned into a full proposal in ~2 min.`;
  }

  if (channel === "copy") {
    return `Follow-up on the ${result.business_name} comparison

Traffic source: ${source}
Deal brief: ${brief}
Regions in the shortlist: ${regions}

Suggested next step: 15-min call to compare the top 2 by rate + guarantee coverage, and pick one to convert into a full lender-ready proposal.`;
  }

  // email default
  return `Subject: Following up on the ${result.business_name} loan comparison

Hi,

I saw you opened the Imali comparison I shared earlier (referrer: ${source}).

Here's a quick recap:
- Applicant: ${result.business_name}
- Industry: ${result.industry_name}
- Requested facility: ${amount} over ${result.tenure_months} months
- Regions compared (${result.columns.length}): ${regions}

Each region ships with its own rate range, guarantee scheme and required documents. Happy to spend 15 minutes walking through the trade-offs and picking the best-fit path — then Imali can turn that region into a full, lender-ready proposal in minutes.

Would sometime this week work for you?

Best regards,`;
}

function buildDeepLink(channel, recipient, message) {
  const msg = message || "";
  if (channel === "email") {
    const subjectMatch = msg.match(/^Subject:\s*(.+)$/im);
    const subject = subjectMatch ? subjectMatch[1].trim() : "Follow-up";
    const body = subjectMatch ? msg.replace(subjectMatch[0], "").trim() : msg;
    return `mailto:${encodeURIComponent(recipient || "")}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  }
  if (channel === "whatsapp") {
    const phone = (recipient || "").replace(/\D+/g, "");
    return phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  }
  if (channel === "linkedin") {
    // LinkedIn has no public DM URL scheme; open profile or messaging search.
    if (recipient) return recipient.startsWith("http") ? recipient : `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(recipient)}`;
    return `https://www.linkedin.com/messaging/`;
  }
  return null;
}
